/**
 * IW3.17.C / IW3.17.C1: Per-user theme (backend ui.theme + localStorage theme:<userId>).
 * No global theme key; user-scoped so logout → login other user gets their theme.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getMyPreference, setMyPreference } from '@/api/userPreferences';
import { QUERY_KEYS } from '@/lib/queryKeys';

export const USER_THEME_PREF_KEY = 'ui.theme';

/** localStorage key per user — IW3.17.C1: never use global 'theme' key */
export const getUserThemeStorageKey = (userId: string) => `theme:${userId}`;

export type UserThemeValue = { is_dark_mode: boolean };

function readUserThemeFromStorage(userId: string): UserThemeValue | null {
  try {
    const raw = localStorage.getItem(getUserThemeStorageKey(userId));
    if (!raw) return null;
    const v = JSON.parse(raw) as { is_dark_mode?: boolean };
    if (typeof v.is_dark_mode !== 'boolean') return null;
    return { is_dark_mode: v.is_dark_mode };
  } catch {
    return null;
  }
}

function writeUserThemeToStorage(userId: string, value: UserThemeValue) {
  try {
    localStorage.setItem(getUserThemeStorageKey(userId), JSON.stringify(value));
  } catch {
    // ignore
  }
}

/** Remove user theme from localStorage (call on logout) */
export function clearUserThemeStorage(userId: string) {
  try {
    localStorage.removeItem(getUserThemeStorageKey(userId));
  } catch {
    // ignore
  }
}

const fetchUserTheme = async (userId: string): Promise<UserThemeValue | null> => {
  const pref = await getMyPreference(USER_THEME_PREF_KEY);
  if (!pref?.value || typeof pref.value !== 'object') return null;
  const v = pref.value as { is_dark_mode?: boolean };
  if (typeof v.is_dark_mode !== 'boolean') return null;
  const value = { is_dark_mode: v.is_dark_mode };
  writeUserThemeToStorage(userId, value);
  return value;
};

export function useUserTheme(userId: string | null) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: QUERY_KEYS.settings.userTheme(userId ?? ''),
    queryFn: () => (userId ? fetchUserTheme(userId) : Promise.resolve(null)),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    initialData: userId ? readUserThemeFromStorage(userId) ?? undefined : undefined,
    initialDataUpdatedAt: 0,
  });

  const setUserTheme = async (value: UserThemeValue) => {
    if (!userId) return;
    writeUserThemeToStorage(userId, value);
    queryClient.setQueryData(QUERY_KEYS.settings.userTheme(userId), value);
    await setMyPreference(USER_THEME_PREF_KEY, value);
  };

  return {
    userTheme: query.data ?? null,
    isLoading: query.isLoading,
    setUserTheme,
    refetch: query.refetch,
  };
}
